import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import api from '../../utils/api'
import { resolveImageUrl } from '../../utils/imageUrl'

/**
 * Pulls one real public item from the archive and displays its actual
 * cataloguing fields — no illustrative stand-in, no invented metadata.
 * Picks the first public item (checked in fetch order) that has both an
 * image and at least two populated fields, so the spotlight isn't blank;
 * the item itself is never fabricated or hand-curated.
 */
const useFeaturedItem = () => {
  const [item, setItem] = useState(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let live = true
    api.get('/items', { params: { isPublic: true, limit: 8 } })
      .then((res) => {
        if (!live) return
        const items = res.data?.data?.items || res.data?.data || []
        const withImage = (Array.isArray(items) ? items : []).find((it) => resolveImageUrl(it.images?.[0]))
        setItem(withImage || null)
        setLoaded(true)
      })
      .catch(() => { if (live) { setItem(null); setLoaded(true) } })
    return () => { live = false }
  }, [])

  return { item, loaded }
}

const buildFields = (item) => {
  if (!item) return []
  const attrs = item.metadata?.attributes || {}
  return [
    { label: 'Category', value: item.category },
    { label: 'Ruler / Issuer', value: attrs.rulerOrIssuer },
    { label: 'Material', value: item.material },
    { label: 'Mint', value: item.mint },
    { label: 'Year', value: item.year },
    { label: 'Provenance', value: attrs.condition },
  ].filter((f) => f.value)
}

const ArtifactShowcaseSection = () => {
  const { item, loaded } = useFeaturedItem()
  const fields = buildFields(item)
  const imageUrl = item ? resolveImageUrl(item.images?.[0]) : null

  if (loaded && !item) return null

  return (
    <section className="relative w-full bg-museum-charcoal text-museum-ivory py-24 md:py-32 overflow-hidden">
      <div className="max-w-[1320px] mx-auto px-6 md:px-10">
        <motion.p
          className="text-[10px] uppercase tracking-[0.35em] text-museum-antique-gold font-sans font-semibold mb-5"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          03 &mdash; Featured Record
        </motion.p>

        {!loaded ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-start">
            <div className="lg:col-span-6 aspect-[4/5] bg-museum-ivory/5 animate-pulse" />
            <div className="lg:col-span-6 space-y-4 pt-4">
              {[0, 1, 2].map((i) => <div key={i} className="h-10 bg-museum-ivory/5 animate-pulse" />)}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-start">
            {/* Object — given generous room, no competing text */}
            <motion.div
              className="lg:col-span-6"
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              <div className="relative aspect-[4/5] max-w-lg mx-auto lg:mx-0">
                <img src={imageUrl} alt={item.name} className="w-full h-full object-contain bg-museum-ivory/[0.03]" />
                <div className="absolute -inset-4 border border-museum-antique-gold/15 pointer-events-none" />
              </div>
            </motion.div>

            {/* Record card */}
            <div className="lg:col-span-6 lg:pt-6">
              <h3
                className="font-editorial italic font-normal text-museum-ivory leading-[0.98] mb-10"
                style={{ fontSize: 'clamp(1.9rem, 3.2vw, 2.75rem)' }}
              >
                {item.name}
              </h3>

              <div className="mb-10">
                {fields.map((f, i) => (
                  <motion.div
                    key={f.label}
                    className="flex items-baseline justify-between gap-6 py-4 border-t border-museum-antique-gold/15 last:border-b"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                  >
                    <span className="text-[10px] uppercase tracking-[0.25em] text-museum-ivory/40 font-sans shrink-0">{f.label}</span>
                    <span className="font-sans text-museum-ivory text-sm text-right">{String(f.value)}</span>
                  </motion.div>
                ))}
              </div>

              <Link
                to={`/items/${item.slug || item._id}`}
                className="inline-flex items-center gap-2 text-museum-antique-gold hover:text-museum-ivory text-[11px] font-sans font-semibold uppercase tracking-[0.22em] transition-colors"
              >
                View Full Record <ArrowRight size={13} />
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default ArtifactShowcaseSection
